import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInlineString, getText } from 'i18n/intl';
import {
  organizationTreeByVirtualIdSelector,
  organizationTreeSelector,
} from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import type { BatchEditOperation } from 'types/batchEditObject.type';
import type { TOrganizationTreeNode, TType } from '@timeedit/types';
import { Form, Modal, Typography } from '@timeedit/ui-components/antd';
import { JobObjectSelection } from '../../hooks/useJobSubmit';
import BulkEditTreeSelect from './BulkEditTreeSelect';
import {
  useBulkEditHasSelection,
  useBulkEditSubmit,
  useBulkEditWarnings,
} from './common';
import './BulkEditField.scss';

type Props = {
  setOpen: (openSection: 'organizations' | false) => void;
  open: boolean;
  selectedObjectsCount?: number;
  objectType: TType | null;
  jobObjectSelection: JobObjectSelection;
  onSubmitSuccess?: () => void;
};

const INDEXED_PROPERTIES = {
  organizations: { id: 0, name: 'organizations' },
};

const createOrganizationOption = (organizationTree: TOrganizationTreeNode) => ({
  value: organizationTree.virtualId,
  title: organizationTree.name,
  disabled: organizationTree?.children.length !== 0,
  children: organizationTree.children?.map((treeNode) =>
    createOrganizationOption(treeNode)
  ),
});

const BulkEditOrganizationModal = ({
  open,
  setOpen,
  selectedObjectsCount,
  objectType,
  jobObjectSelection,
  onSubmitSuccess,
}: Props) => {
  const [sendingBatch, setSendingBatch] = useState(false);
  const [form] = Form.useForm();
  const organizationTree = useAppSelector(organizationTreeSelector);
  const organizationTreeByVirtualId = useAppSelector(
    organizationTreeByVirtualIdSelector
  );
  const hasSelection = useBulkEditHasSelection(form);
  const fieldNameMap = useMemo(
    () => ({
      [INDEXED_PROPERTIES.organizations.id]: {
        name: getText('object_manager.bulk-edit-modal.organizations.title'),
      },
    }),
    []
  );
  const warnings = useBulkEditWarnings(
    form,
    fieldNameMap,
    selectedObjectsCount ?? 0
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const buildOperations = useCallback(
    (validationResult: Record<string, any>) =>
      transformFormDataToBatchEditOperation(
        validationResult,
        organizationTreeByVirtualId
      ),
    [organizationTreeByVirtualId]
  );

  const onSubmit = useBulkEditSubmit({
    form,
    setSendingBatch,
    setOpen,
    selection: jobObjectSelection,
    operationType: 'EDIT_ORGANIZATIONS',
    buildOperations,
    successMessage: getText('object_manager.bulk-edit-modal.success-message'),
    onSuccess: onSubmitSuccess,
  });

  const organizationOptions = useMemo(
    () =>
      organizationTree.children?.map((treeNode) =>
        createOrganizationOption(treeNode)
      ) ?? [],
    [organizationTree]
  );

  const operationSections = useMemo(
    () => [
      <BulkEditTreeSelect
        key="organizations"
        allowMultipleSearch
        actionList={[
          'keep',
          'add',
          'findReplace',
          'replaceAll',
          'removeAll',
          'removeSpecific',
        ]}
        field={{
          id: INDEXED_PROPERTIES.organizations.id,
          name: 'organizations',
          multiple: true,
          treeData: organizationOptions,
          description: undefined,
          editable: true,
          min: undefined,
          max: undefined,
          unique: false,
          mandatory: false,
        }}
        form={form}
      />,
    ],
    [form, organizationOptions]
  );

  if (!objectType) {
    return null;
  }

  return (
    <Modal
      open={open}
      okText={getText('object_manager.bulk-edit-modal.okText')}
      onOk={onSubmit}
      cancelText={getText('cancel')}
      onCancel={() => setOpen(false)}
      okButtonProps={{
        loading: sendingBatch,
        disabled: hasSelection === false,
      }}
      title={
        <div className="bulk-edit-modal-title-wrapper">
          {getText('object_manager.bulk-edit-modal.organizations.title')}
          <Typography.Text>
            {getInlineString(
              'object_manager.bulk-edit-modal.subtitle',
              selectedObjectsCount,
              objectType.name
            )}
          </Typography.Text>
        </div>
      }
    >
      <Form form={form} layout="horizontal" requiredMark={false}>
        <div className="bulk-edit-wrapper">{operationSections}</div>
        {warnings}
      </Form>
    </Modal>
  );
};

const transformFormDataToBatchEditOperation = (
  formData: Record<string, any>,
  mapOrgId: Record<string, string>
): BatchEditOperation[] => {
  const operations: BatchEditOperation[] = [];
  const action = formData[INDEXED_PROPERTIES.organizations.id];

  if (action && action !== 'keep') {
    switch (action) {
      case 'add':
        operations.push({
          category: 'organizations',
          operation: 'add',
          value: formData[`${INDEXED_PROPERTIES.organizations.id}_add`].map(
            (id: string) => mapOrgId[id]
          ),
        });
        break;
      case 'findReplace':
        operations.push({
          category: 'organizations',
          rule:
            formData[`${INDEXED_PROPERTIES.organizations.id}_rule`] ??
            'matchAll',
          operation: 'findReplace',
          searchValue: formData[
            `${INDEXED_PROPERTIES.organizations.id}_replace`
          ].map((id: string) => mapOrgId[id]),
          value: formData[`${INDEXED_PROPERTIES.organizations.id}_with`].map(
            (id: string) => mapOrgId[id]
          ),
        });
        break;
      case 'replaceAll':
        operations.push({
          category: 'organizations',
          operation: 'replaceAll',
          value: formData[
            `${INDEXED_PROPERTIES.organizations.id}_replaceAll`
          ].map((id: string) => mapOrgId[id]),
        });
        break;
      case 'removeSpecific':
        operations.push({
          category: 'organizations',
          operation: 'removeSpecific',
          searchValue: formData[
            `${INDEXED_PROPERTIES.organizations.id}_removeSpecific`
          ].map((id: string) => mapOrgId[id]),
        });
        break;
      case 'removeAll':
        operations.push({
          category: 'organizations',
          operation: 'removeAll',
        });
        break;
    }
  }
  return operations;
};

export default BulkEditOrganizationModal;
